// Cold-start agent roster cache — persist the last-known daemon roster (keyed by
// workspace id) so a fresh launch / workspace switch can render the user's agents
// OFFLINE (greyed, via ChannelSidebar's agentDotClass) immediately, instead of a
// blank list until the daemon connects. The daemon-proxied cyborg:list_agents
// returns [] while the daemon is down, and on cold start there is no in-memory
// roster to fall back to — unlike humans (workspaceState.members), which are
// server-durable. localStorage is the only daemon-independent source: cybos and
// agents are both DAEMON_FORWARD, so nothing server-side returns them offline.
//
// Merge semantics live in nextAgentSnapshot (daemon-status.ts): a non-empty live
// list — or ANY list while a daemon is reachable — always wins, so a genuinely
// removed agent drops the moment the daemon returns the real roster. Only the
// offline/cold window shows cached rows.
//
// OWNER-KEYED rather than logout-wiped, exactly as session-list-cache.ts (#2508)
// does for the archived-session page — one idiom, not two. disconnectFromServer
// clears workspaceState in memory (app.svelte.ts) but wipes no localStorage, and a
// crash / force-quit never runs logout at all, so these rows (agent names, cybo
// names/avatars, daemon ids) would outlive the account that produced them on a
// shared device: user B's cold start painted user A's roster. The guard has to hold
// when no logout ever ran, so the owner is stored WITH the rows and a mismatch reads
// as a MISS. Keyed on the EMAIL for the reason cacheInit documents (cache/index.ts):
// one human holds two ids here (the cloud relay's PG uuid vs a daemon-direct
// canonicalUserId), so an id compare reads a cloud↔self-host switch as a different
// user and throws away a valid cache.
//
// Kept as small, storage-injected functions (no live-store imports) so the
// read/write logic runs under plain-node vitest.

import type { Agent } from "$lib/plugins/agents/types.js";

// Minimal storage surface — the app passes `localStorage`; tests pass an in-memory
// fake. Nullable so an SSR/prerender pass (no localStorage) is a clean no-op.
export type RosterStore = Pick<Storage, "getItem" | "setItem"> | null;

const KEY_PREFIX = "cyborg7:agentRoster:";

export function rosterCacheKey(workspaceId: string): string {
  return KEY_PREFIX + workspaceId;
}

// The stored envelope: the rows PLUS the account they belong to.
interface CachedRoster {
  owner: string;
  agents: Agent[];
}

// Normalize an email into a cache owner key. Same rule as cacheInit's ownerKey
// (cache/index.ts), and the single definition sessionCacheOwner (session-list-cache.ts)
// delegates to, so the guards can't disagree about who a cache belongs to.
export function rosterCacheOwner(email: string | null | undefined): string {
  return typeof email === "string" ? email.trim().toLowerCase() : "";
}

// Parse a persisted roster blob for `owner`. Returns [] for absent / malformed /
// non-object input, for a blob written by a DIFFERENT account, and for an unknown
// owner — so a first-ever launch, a corrupted entry, and a shared device all render
// no cold-start rows rather than throwing or leaking. Drops entries without the
// agentId that the row render + dedupe key on, so a partial-corrupt cache can't
// render broken rows.
//
// A pre-envelope blob (a bare Agent[], the shape written before the owner guard) has
// no owner, so it reads as a miss: we cannot prove whose rows those are. The first
// launch after upgrading therefore repaints once from the live roster and is stable
// after — the same one-time cost cacheInit's upgrade note documents.
export function parseRosterCache(raw: string | null | undefined, owner: string): Agent[] {
  if (!raw || !owner) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return []; // intentional: a corrupt cache → no cold-start rows; the live fetch backfills.
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return [];
  const envelope = parsed as Partial<CachedRoster>;
  // Owner mismatch (or a pre-envelope blob with no owner at all) is a MISS, not a read.
  if (envelope.owner !== owner) return [];
  if (!Array.isArray(envelope.agents)) return [];
  return envelope.agents.filter(
    (a): a is Agent =>
      typeof a === "object" &&
      a !== null &&
      typeof (a as { agentId?: unknown }).agentId === "string",
  );
}

// Never persist an EMPTY roster that arrived while the daemon is offline — that is
// the daemon-proxied "[]" meaning "can't reach the daemon", not "no agents".
// Persisting it would wipe the last-known roster the next cold start needs. A
// non-empty roster is always real; an empty one is authoritative only when a
// daemon is reachable (a genuine clear → drop the cache so nothing ghosts).
//
// Takes `readonly unknown[]` because the rule is about the LIST, not the row shape:
// it only reads `.length`, and every DAEMON_FORWARD list has the same ambiguous
// empty. session-list-cache.ts reuses it verbatim for the archived-session page so
// that invariant keeps one definition here instead of being restated per caller.
export function shouldPersistRoster(rows: readonly unknown[], daemonConnected: boolean): boolean {
  return rows.length > 0 || daemonConnected;
}

// Drop rows homed on a daemon the SERVER no longer returns (CYBORG-680 layer 4).
// The cache is hydrated on cold start before any daemon answers, and every row
// carries a `daemonId` that surfaces read back as a live target — AgentComposer
// and ArchivedSessionRow both call `fetchProviders(agent.daemonId)` off it. So a
// daemon deleted from the workspace kept being queried from localStorage on
// every launch, forever, with no way to recover from the UI. The workspace
// daemon list is relay/DB-served (not daemon-forwarded), so it IS authoritative
// — but only when it actually came back: an empty list is treated as "no signal"
// and prunes nothing, so a failed/blank fetch can never wipe the roster.
export function pruneRosterForDaemons<T extends { daemonId?: string | null }>(
  agents: T[],
  knownDaemonIds: readonly string[],
): T[] {
  if (knownDaemonIds.length === 0) return agents;
  const known = new Set(knownDaemonIds);
  return agents.filter((a) => !a.daemonId || known.has(a.daemonId));
}

export function loadRosterCache(
  store: RosterStore,
  workspaceId: string,
  email: string | null | undefined,
): Agent[] {
  const owner = rosterCacheOwner(email);
  if (!store || !owner) return [];
  try {
    return parseRosterCache(store.getItem(rosterCacheKey(workspaceId)), owner);
  } catch {
    return []; // intentional: storage disabled/blocked → no cold-start rows this launch.
  }
}

export function saveRosterCache(
  store: RosterStore,
  workspaceId: string,
  email: string | null | undefined,
  agents: Agent[],
  daemonConnected: boolean,
): void {
  const owner = rosterCacheOwner(email);
  // No owner ⇒ we can't prove whose rows these are, so we never write them.
  if (!store || !owner || !shouldPersistRoster(agents, daemonConnected)) return;
  try {
    store.setItem(
      rosterCacheKey(workspaceId),
      JSON.stringify({ owner, agents } satisfies CachedRoster),
    );
  } catch {
    // intentional: storage full/disabled → skip caching; cold start just shows nothing until the daemon connects.
  }
}
